import { useAtom } from "jotai";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../shared/api/yenta-api";
import { AdapexAds } from "../shared/components/adapex-ads.component";
import { ArcadeHeader } from "../shared/components/arcade-header/arcade-header.component";
import { ArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/arcade-instructions-modal.component";
import { useArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/use-arcade-instructions-modal";
import { useGaSweepstakesEventTracker } from "../shared/eventsTracker/ga-tracker/ga-events-tracker";
import { GaSweepstakesEventActions } from "../shared/eventsTracker/ga-tracker/ga-tracker.types";
import { ArcadeGames } from "../shared/types";
import {
  getArcadeGameImageUrl,
  retrieveFromSessionStorage,
  saveInSessionStorage,
} from "../shared/utils";

import { QuizHeader } from "./components/quiz-header/quiz-header.component";
import { QuizFooter } from "./components/shared/footer.component";
import { QuestionAds } from "./components/shared/question-ads.component";
import { QuizQuestions } from "./components/shared/questions/questions.component";
import { EXTRA_ENTRIES_PER_SMS_ACCEPTANCE } from "./widget.constants";
import {
  instructionsModalAtom,
  quizAtom,
  remindMeAtom,
  smsAtom,
} from "./widget.state";

const enable_quiz_ads = window.FKL.enable_quiz_ads;
const show_adapex_ads =
  window.FKL.show_adapex_ads != undefined &&
  window.FKL.show_adapex_ads.toLocaleLowerCase() === "true";
const questionsPerPage = window.FKL.questionsPerPage;

export function Widget() {
  const [quiz] = useAtom(quizAtom);

  return quiz.canPlay ? (
    <WidgetContent />
  ) : (
    <div className="error-message">
      You&apos;ve already answered the Quiz. To earn new extra entires try
      opt-in to the sweep again.{" "}
      <a
        href={window.theme.Routing.redirectToNextPage(
          window.FKL.collectionMetafields!,
          window.FKL.collectionHandle.replace("-quiz", ""),
          "quiz"
        )}
      >
        <strong>Go to next page.</strong>
      </a>
    </div>
  );
}

function WidgetContent() {
  const [quiz] = useAtom(quizAtom);
  const [remindMe] = useAtom(remindMeAtom);
  const [sms] = useAtom(smsAtom);
  const [instructionsModal, setInstructionsModal] = useAtom(
    instructionsModalAtom
  );
  const handleWidgetError = useErrorHandler();
  const [sweepstakesPlayQuizState, setSweepstakesPlayQuizState] = useState<
    boolean
  >(false);
  const [
    sweepstakesCompleteQuizState,
    setSweepstakesCompleteQuizState,
  ] = useState<boolean>(false);
  const gaSweepstakesEventTracker = useGaSweepstakesEventTracker();
  const [
    showInstructionsModal,
    handleHideInstructionsModal,
  ] = useArcadeInstructionsModal(ArcadeGames.Quiz);
  const arcadeAvailableGames = window.FKL.arcadeAvailableGamesInfo;

  const [lastQuestionAnswered] = useMemo(
    () =>
      quiz.questions
        .filter((question) =>
          question.options.some((option) => option.isSelected)
        )
        .slice(-1),
    [quiz.questions]
  );

  const quizExtraEntries = useMemo(
    () =>
      quiz.questions.reduce(
        (acc, question) =>
          question.options.some(
            (option) => option.isSelected && option.isCorrect
          )
            ? acc + 1
            : acc,
        0
      ),
    [quiz.questions]
  );

  const sessionEntries = useMemo(() => {
    const SWEEPSTAKE_SESSION_ENTRIES = "sweepstakeSessionEntries";
    const sweepstakeSessionEntries = retrieveFromSessionStorage(
      SWEEPSTAKE_SESSION_ENTRIES
    );
    if (!sweepstakeSessionEntries) {
      saveInSessionStorage(SWEEPSTAKE_SESSION_ENTRIES, 0);
      return 0;
    }
    if (quizExtraEntries > 0) {
      saveInSessionStorage(
        SWEEPSTAKE_SESSION_ENTRIES,
        Number(sweepstakeSessionEntries) + 1
      );
      return Number(sweepstakeSessionEntries) + 1;
    }
    return Number(sweepstakeSessionEntries);
  }, [quizExtraEntries]);

  const redeemedEntries = useCallback(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return submission ? Number(submission.data.redeemedEntries) || 0 : 0;
  }, []);

  const initialArcadeQuestionsAnswered = useMemo(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return Number(submission?.data.arcadeQuestionsAnswered) || 0;
  }, []);

  const arcadeQuestionsAnswered = useMemo(() => {
    return quiz.questions.reduce((acc, question) => {
      let isFinished = true;

      isFinished = isFinished && question.options.some((q) => q.isSelected);
      isFinished =
        isFinished &&
        (question.cpl?.isAccepted !== null || !question.cpl?.title);

      if (question.cpl?.formPath && question.cpl.isAccepted === true) {
        isFinished = isFinished && question.cpl.isTacAccepted;
      }

      return acc + (isFinished ? 1 : 0);
    }, initialArcadeQuestionsAnswered);
  }, [initialArcadeQuestionsAnswered, quiz.questions]);

  useEffect(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    if (submission) {
      const SAMPLE_BOX_PRODUCT = "arcadeProductData";
      const SAMPLE_BOX_CODE = "boxSamplesCode";
      let sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") ?? "";

      const lastQuestionAnsweredId = lastQuestionAnswered
        ? lastQuestionAnswered.id
        : submission.data.lastQuestionAnsweredId;

      const lastQuestionId = quiz.questions[quiz.questions.length - 1].id;
      const isLastQuestionAnswered = lastQuestionAnsweredId === lastQuestionId;

      const smsSelectedCompanies = [
        ...new Set(
          (quiz.prevSmsSelectedCompanies ?? "")
            .split(",")
            .filter((company) => company !== "")
            .concat(sms.selectedCompanies)
        ),
      ].join(",");

      const patchData = {
        arcadeQuestionsAnswered: quiz.isArcade ? arcadeQuestionsAnswered : 0,
        quizExtraEntries:
          quizExtraEntries +
            Number(
              quiz.isArcade ? quiz.allTimePoints! : quiz.prevExtraEntries!
            ) || 0,
        quizAcceptedCpls: quiz.questions
          .reduce<string[]>(
            (acc, question) => {
              if (question.cpl) {
                let isAccepted = false;
                if (question.cpl.isAccepted) isAccepted = true;
                if (question.cpl.formPath)
                  isAccepted = isAccepted && question.cpl.isTacAccepted;
                if (isAccepted && !acc.includes(question.cpl.name)) {
                  return [...acc, question.cpl.name];
                } else if (
                  question.cpl.isAccepted === false &&
                  !question.cpl.registeredCpl &&
                  acc.includes(question.cpl.name)
                ) {
                  return acc.filter((cplName) => {
                    return cplName != question.cpl?.name;
                  });
                }
              }

              return acc;
            },
            ((submission.data.quizAcceptedCpls as string) ?? "")
              .split(",")
              .filter((cpl) => {
                return cpl;
              })
          )
          .join(","),
        lastQuestionAnsweredId,
        optinReminder: remindMe,
        smsMobilePhone: sms.phoneNumber,
        smsSelectedCompanies,
        smsExtraEntries:
          (quiz.prevSmsExtraEntries ?? 0) +
          sms.selectedCompanies.length * EXTRA_ENTRIES_PER_SMS_ACCEPTANCE,
        smsTermsAndConditions: Boolean(smsSelectedCompanies.length),
        quizCompletionsCount: !quiz.isArcade
          ? Number(quiz.quizCompletionsCount ?? 0) +
            (isLastQuestionAnswered ? 1 : 0)
          : 0,
      };

      if (
        quiz.isArcade &&
        !lastQuestionAnswered &&
        sessionStorage.getItem(SAMPLE_BOX_PRODUCT) &&
        sessionStorage.getItem(SAMPLE_BOX_CODE)
      ) {
        sweepFormUrl =
          document
            .querySelector("#FormSweepstake")
            ?.getAttribute("data-form-url") ?? "";
        const formio = new window.Formio(
          `${sweepFormUrl}/submission/${submission._id}`
        );
        formio
          .saveSubmission({ _id: submission._id })
          .then((uptodaySubmission: any) => {
            formio
              .saveSubmission({
                _id: uptodaySubmission._id,
                data: {
                  ...uptodaySubmission.data,
                  ...patchData,
                },
              })
              .then((submission: any) => {
                window.theme.Sweepstake.prototype.storeSubmission(submission);
              });
          });
        sessionStorage.removeItem(SAMPLE_BOX_PRODUCT);
        sessionStorage.removeItem(SAMPLE_BOX_CODE);
      } else {
        updateFormioSubmission(
          sweepFormUrl,
          {
            ...submission,
            data: {
              ...submission.data,
              ...patchData,
            },
          },
          handleWidgetError
        ).then((submission: any) => {
          window.theme.Sweepstake.prototype.storeSubmission(submission);
          //Verify if question has nextPageUrl
          if (
            lastQuestionAnswered &&
            (lastQuestionAnswered.cpl?.isAccepted !== null ||
              !lastQuestionAnswered.cpl?.title) &&
            lastQuestionAnswered.nextPageUrl != null
          ) {
            window.location.href = window.theme.Routing.redirectToNextPage(
              { next_page_url: lastQuestionAnswered.nextPageUrl },
              null
            );
          }
        });
      }
    }
  }, [
    arcadeQuestionsAnswered,
    quiz.allTimePoints,
    quiz.prevExtraEntries,
    quiz.prevSmsExtraEntries,
    quiz.prevSmsSelectedCompanies,
    quiz.questions,
    quiz.isArcade,
    quiz.quizCompletionsCount,
    quizExtraEntries,
    remindMe,
    sms.phoneNumber,
    sms.selectedCompanies,
    lastQuestionAnswered,
    handleWidgetError,
  ]);

  useEffect(() => {
    if (!quiz.isArcade) {
      const lastQuestionAnsweredId = lastQuestionAnswered
        ? lastQuestionAnswered.id
        : "";
      if (!sweepstakesPlayQuizState) {
        const firstQuestionId = quiz.questions[0].id;
        if (firstQuestionId === lastQuestionAnsweredId) {
          gaSweepstakesEventTracker(GaSweepstakesEventActions.PLAY_QUIZ);
          setSweepstakesPlayQuizState(true);
        }
      } else if (!sweepstakesCompleteQuizState) {
        const lastQuestionId = quiz.questions[quiz.questions.length - 1].id;
        if (lastQuestionId === lastQuestionAnsweredId) {
          gaSweepstakesEventTracker(GaSweepstakesEventActions.COMPLETE_QUIZ);
          setSweepstakesCompleteQuizState(true);
        }
      }
    }
  }, [
    gaSweepstakesEventTracker,
    lastQuestionAnswered,
    quiz.questions,
    sweepstakesPlayQuizState,
    quiz.isArcade,
    sweepstakesCompleteQuizState,
  ]);

  useEffect(() => {
    setInstructionsModal(showInstructionsModal);
  }, [setInstructionsModal, showInstructionsModal]);

  useLayoutEffect(() => {
    const mainContent = document.querySelector(
      ".main-content"
    ) as HTMLElement | null;
    if (mainContent) mainContent.style.paddingTop = "0px";

    const container = document.querySelector(
      ".product-template__container"
    ) as HTMLElement;
    if (container) container.classList.remove("page-width");
  }, []);

  return (
    <div className="quiz--single-page">
      {quiz.isArcade ? (
        <ArcadeHeader
          allTimeEntries={quiz.allTimePoints!}
          extraEntries={quizExtraEntries}
          sessionEntries={sessionEntries}
          redeemedEntries={redeemedEntries()}
        />
      ) : (
        <QuizHeader />
      )}
      {enable_quiz_ads && show_adapex_ads ? (
        <div className="quiz--single-page__ads quiz--single-page__ads--left">
          <AdapexAds adunit="fkl_300v_Sticky_Left" />
        </div>
      ) : (
        <div />
      )}
      <QuizQuestions showAds={enable_quiz_ads && show_adapex_ads} />
      {enable_quiz_ads && show_adapex_ads ? (
        <div className="quiz--single-page__ads quiz--single-page__ads--right">
          <AdapexAds adunit="fkl_300v_Sticky" />
        </div>
      ) : (
        <div />
      )}

      <QuizFooter />

      {enable_quiz_ads && show_adapex_ads && questionsPerPage === 1 && (
        <div className="quiz__single__question-ad-container">
          <QuestionAds questionIndex={1} />
        </div>
      )}

      {quiz.isArcade ? (
        <ArcadeInstructionsModal
          isOpen={instructionsModal}
          title="Shopping Trivia"
          subtitle=""
          image={getArcadeGameImageUrl(
            arcadeAvailableGames,
            document.location.href
          )}
          instructions={
            <>
              Answer questions about products and brands before the timer runs
              out. You will have <strong>20 seconds</strong> to answer each
              question. You will earn{" "}
              <strong>+1 point for every correct answer.</strong> Good luck!
            </>
          }
          onHideInstructionsModal={handleHideInstructionsModal}
        />
      ) : null}
    </div>
  );
}

declare abstract class Sweepstake {
  checkHash(): Promise<any>;
  checkCplExtraEntriesHash(): Promise<any>;
  retrieveSubmission(): {
    _id: string;
    data: Record<string, any>;
    modified?: string;
  } | null;
  storeSubmission(submission: any): Promise<any>;
  storeSubmissionHash(submission: any): Promise<any>;
  hashChecks: {
    NO_SUBMISSION: Error;
  };
}

declare abstract class RecaptchaUtils {
  static updateSubmissionToken(data: any): Promise<any>;
}

declare abstract class Routing {
  static redirectToNextPage(
    metadata: any,
    handle: string | null,
    extra?: any
  ): string;
}

declare abstract class ShopifyFormioProxy {
  static updateFormioSubmission(
    formPath: string,
    submissionObj: any
  ): Promise<any>;
}

declare global {
  interface FKL {
    wordSearchSlug: string;
    quizSlug: string;
    yentaServicesApiUrl: string;
    assetWidgetUrl: string;
    enable_quiz_ads: boolean;
    show_adapex_ads: string;
    allowTermsAndConditions: string;
    questionsPerPage: number;
    allowOptInReminder: boolean;
    collectionMetafields: Record<string, unknown>;
    collectionHandle: string;
    sweepstakePrice: string;
    sweepstakeTitle: string;
    shouldDoublePoints: boolean;
    cplQuizTitle: string
  }

  interface Theme {
    RecaptchaUtils: typeof RecaptchaUtils;
    Sweepstake: typeof Sweepstake;
    Routing: typeof Routing;
    ShopifyFormioProxy: typeof ShopifyFormioProxy;
  }
}

window.theme.Sweepstake.prototype.retrieveSubmission();
